// ============================================================================
// adService — rewarded-ad adapter (STUB)
// ----------------------------------------------------------------------------
// Phase 2 ships a simulated, timed ad player behind a stable `AdProvider`
// interface so a real network (AdMob web, a video SSP, etc.) drops in later
// without touching the spin/journey UI. `playAd` resolves when an ad "finishes"
// and rejects if it cannot be shown; the journey state machine
// (../utils/adRewardJourney) decides what a rejection/dropout means.
// ============================================================================

export interface AdPlaybackContext {
  index: number
  total: number
  /** Called ~30x/sec with 0..1 so the UI can render a progress ring. */
  onProgress?: (fraction: number) => void
  signal?: AbortSignal
}

export interface AdProvider {
  readonly id: string
  /** Resolves on clean completion; rejects if aborted or unavailable. */
  playAd: (context: AdPlaybackContext) => Promise<void>
}

const SIMULATED_AD_DURATION_MS = 2200
const PROGRESS_TICK_MS = 60

/**
 * Simulated provider: a timed delay with progress ticks, abortable mid-play so
 * the journey's dropout path (blur/close) can cancel cleanly.
 */
export const simulatedAdProvider: AdProvider = {
  id: 'SIMULATED',
  playAd: ({ onProgress, signal }: AdPlaybackContext) =>
    new Promise<void>((resolve, reject) => {
      if (signal?.aborted) {
        reject(new DOMException('Ad playback aborted', 'AbortError'))
        return
      }

      const startedAt = performance.now()
      let interval: number | null = null

      const cleanup = () => {
        if (interval !== null) window.clearInterval(interval)
        interval = null
        signal?.removeEventListener('abort', onAbort)
      }

      const onAbort = () => {
        cleanup()
        reject(new DOMException('Ad playback aborted', 'AbortError'))
      }

      signal?.addEventListener('abort', onAbort, { once: true })

      interval = window.setInterval(() => {
        const elapsed = performance.now() - startedAt
        const fraction = Math.min(1, elapsed / SIMULATED_AD_DURATION_MS)
        onProgress?.(fraction)
        if (fraction >= 1) {
          cleanup()
          resolve()
        }
      }, PROGRESS_TICK_MS)
    }),
}

/** The provider the app uses today. Swap this to switch networks. */
export const activeAdProvider: AdProvider = simulatedAdProvider
